import { createRoute, Link, useParams } from '@tanstack/react-router';
import { IconChevronLeft } from '@tabler/icons-react';
import { Route as appRoute } from '../../app.js';
import { useCategories } from '../../../hooks/useCategories.js';
import { useCourses } from '../../../hooks/useCourses.js';
import { CourseCard } from '../../../components/courses/CourseCard.js';

export const Route = createRoute({
  getParentRoute: () => appRoute,
  path: '/courses/$category/$subCategory',
  component: SubCategoryPage,
});

function SubCategoryPage() {
  const { category, subCategory } = useParams({
    from: '/app/courses/$category/$subCategory',
  });
  const { data: catData } = useCategories();
  const { data, isLoading, error } = useCourses(category, subCategory);

  const cat = catData?.categories.find((c) => c.category === category);
  const sub = cat?.subCategories.find((s) => s.slug === subCategory);

  return (
    <div className="max-w-6xl">
      {/* Breadcrumb */}
      <Link
        to="/app/courses/$category"
        params={{ category }}
        className="inline-flex items-center gap-1 text-xs text-muted hover:text-navy transition mb-3"
      >
        <IconChevronLeft size={14} />
        {cat?.label ?? category}
      </Link>

      <h1 className="font-serif text-4xl tracking-tighter2 mb-2">
        {sub?.label ?? subCategory}
      </h1>
      <p className="text-muted text-sm mb-8">
        {data?.items.length ?? 0} course{(data?.items.length ?? 0) === 1 ? '' : 's'} in your catalogue
      </p>

      {isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[0, 1, 2].map((i) => (
            <div key={i} className="surface p-5 animate-pulse">
              <div className="h-4 w-20 bg-cream-deep rounded mb-4" />
              <div className="h-6 w-3/4 bg-cream-deep rounded mb-2" />
              <div className="h-3 w-1/2 bg-cream-deep rounded mb-6" />
              <div className="h-10 w-full bg-cream-deep rounded" />
            </div>
          ))}
        </div>
      )}

      {error && (
        <div className="surface p-5 text-danger text-sm">Couldn't load courses.</div>
      )}

      {!isLoading && data && data.items.length === 0 && (
        <div className="surface-soft p-8 text-center">
          <h3 className="font-serif text-lg text-ink mb-1">No courses here yet</h3>
          <p className="text-sm text-muted max-w-md mx-auto">
            Phase 1A seeds a handful of sample courses across categories. The super-admin
            curation UI lands in Phase 1B and lets you add as many as you want.
          </p>
        </div>
      )}

      {!isLoading && data && data.items.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.items.map((c) => (
            <CourseCard key={c.id} course={c} />
          ))}
        </div>
      )}
    </div>
  );
}
